import React, { useEffect, useMemo } from "react";
import { endOfQuarter, format, startOfMonth, startOfQuarter } from "date-fns";
import { Box, styled } from "@mui/material";
import { BarChartMode, Range, BarChartItem } from "../types";
import { BarSection } from "./BarSection";

const BarChartContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "transitionDirection",
})<{ transitionDirection: "left" | "right" | null }>(
  ({ theme, transitionDirection }) => ({
    display: "flex",
    width: "100%",
    flexShrink: 0,
    alignItems: "center",
    gap: theme.spacing(1),
    overflow: "auto",
    marginBottom: theme.spacing(1),
    transform: `translateX(${
      transitionDirection === "left"
        ? "0"
        : transitionDirection === "right"
          ? "-200%"
          : "-100%"
    })`,
    transition: transitionDirection ? "transform 0.3s ease-in-out" : "none",

    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(4),
    },
  })
);

const formatData = (
  data: BarChartItem[],
  mode: BarChartMode,
  mainPeriod: Range
) => {
  if (mode === "quarterly") {
    return groupByQuarter(data);
  }

  if (mode === "yearly") {
    return groupByYear(data, mainPeriod);
  }

  return formatMonthly(data);
};

const formatMonthly = (data: BarChartItem[]) => {
  return data.map((x) => ({
    ...x,
    label: x.label.toLocaleUpperCase().slice(0, 3),
  }));
};

const groupByQuarter = (data: BarChartItem[]) => {
  const groupedData = data.reduce(
    (acc, item) => {
      const month = item.startDate.getMonth();
      const quarter = Math.floor(month / 3) + 1;
      const year = item.startDate.getFullYear();
      const quarterLabel =
        data.length <= 12 ? `Q${quarter}` : `Q${quarter} ${year}`;

      if (acc[quarterLabel]) {
        acc[quarterLabel].value += item.value;
        acc[quarterLabel].value2 += item.value2;
      } else {
        acc[quarterLabel] = {
          ...item,
          id: quarterLabel,
          label: quarterLabel,
          startDate: startOfQuarter(item.startDate),
          endDate: endOfQuarter(item.endDate),
        };
      }

      return acc;
    },
    {} as Record<string, BarChartItem>
  );

  return Object.values(groupedData);
};

const groupByYear = (data: BarChartItem[], period: Required<RangeType>) => {
  const groupedData = data.reduce(
    (acc, item) => {
      const year = item.startDate.getFullYear();
      const yearLabel = `${year}`;

      if (acc[yearLabel]) {
        acc[yearLabel].value += item.value;
        acc[yearLabel].value2 += item.value2;
      } else {
        acc[yearLabel] = {
          id: yearLabel,
          label: yearLabel,
          value: item.value,
          value2: item.value2,
          startDate: new Date(`${year}-01-01`),
          endDate: new Date(`${year}-12-31`),
        };
      }

      return acc;
    },
    {} as Record<string, BarChartItem>
  );

  return Object.values(groupedData);
};

type Props = {
  data: BarChartItem[];
  mode: BarChartMode;
  mainPeriod: Range;
  onBarClick: (item: BarChartItem, changeDateRange?: boolean) => void;
  selectedBar: string | null;
  isAnimationEnabled: boolean;
  transitionDirection: "left" | "right" | null;
  intervalType: IntervalType;
  currentYearBlock?: boolean;
};

export const BarChartYear = ({
  data,
  mode,
  mainPeriod,
  onBarClick,
  selectedBar,
  isAnimationEnabled,
  transitionDirection,
  currentYearBlock = false,
}: Props) => {
  const formattedData = useMemo(
    () => formatData(data, mode, mainPeriod),
    [data, mode, mainPeriod]
  );

  const maxNumber = useMemo(
    () =>
      formattedData.reduce(
        (acc, item) => Math.max(acc, Math.max(item.value, item.value2)),
        0
      ),
    [formattedData]
  );

  useEffect(() => {
    if (selectedBar || !currentYearBlock || formattedData.length === 0) return;

    const currentMonthId = format(startOfMonth(new Date()), "yyyy-MM-dd");
    const lastItem = formattedData[formattedData.length - 1];
    const currentMonthItem =
      formattedData.find((x) => x.id === currentMonthId) ?? lastItem;

    const isMonthlyMode = mode === "monthly";
    onBarClick(isMonthlyMode ? currentMonthItem : lastItem, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedData]);

  return (
    <BarChartContainer transitionDirection={transitionDirection}>
      {formattedData.map((item) => {
        return (
          <BarSection
            key={item.id}
            fullWidth
            onClick={() => onBarClick(item)}
            item={item}
            isSelected={selectedBar === item.id}
            maxNumber={maxNumber}
            isAnimationEnabled={isAnimationEnabled}
          />
        );
      })}
    </BarChartContainer>
  );
};
